import { createClient } from '@/lib/supabase';
import { getLibraryContext, setLibraryOwner, type LibraryContext } from '@/native-shell/library';
import { getNativeTrackingPlugin } from '@/lib/native/tracking-bridge';
import { clearLegacyDownloads } from './legacy';

let ready: Promise<LibraryContext> | null = null;
let pending: Promise<LibraryContext> | null = null;
export const OFFLINE_ACCOUNT_EVENT = 'peakwave-offline-account';
let listeningForAccounts = false;
let listeningForAuth = false;
const recordingCheckpoints = new Set<() => Promise<void>>();
export function registerRecordingCheckpoint(checkpoint: () => Promise<void>): () => void {
  recordingCheckpoints.add(checkpoint);
  return () => { recordingCheckpoints.delete(checkpoint); };
}
async function checkpointRecording() {
  await Promise.all([...recordingCheckpoints].map(checkpoint => checkpoint()));
}

/** The owner is a local privacy boundary, never authorisation for a server request. */
export function initialiseOfflineAccount(): Promise<LibraryContext> {
  if (ready) return ready;
  if (!listeningForAccounts) {
    listeningForAccounts = true;
    window.addEventListener('storage', event => {
      if (event.key === 'peakwave-offline-generation') window.dispatchEvent(new Event(OFFLINE_ACCOUNT_EVENT));
    });
  }
  const client = createClient();
  const update = (owner: string) => {
    pending = (pending?.catch(() => undefined) ?? Promise.resolve()).then(async () => {
      const before = await getLibraryContext();
      if (before.owner !== owner) {
        await getNativeTrackingPlugin()?.stop();
        try { await checkpointRecording(); }
        catch (error) {
          // Hide old account presentation even when its final journal write fails.
          window.dispatchEvent(new Event(OFFLINE_ACCOUNT_EVENT));
          throw error;
        }
      }
      const context = await setLibraryOwner(owner);
      if (before.owner !== owner) await clearLegacyDownloads();
      if (before.owner !== owner) window.dispatchEvent(new Event(OFFLINE_ACCOUNT_EVENT));
      return context;
    });
    return pending;
  };
  ready = client.auth.getSession().then(({ data, error }) => {
    if (error) throw new Error('Account details could not be checked. Reconnect and retry.');
    return update(data.session?.user.id ?? 'device');
  });
  if (!listeningForAuth) client.auth.onAuthStateChange((event, session) => {
    if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') return;
    // Supabase invokes listeners under its auth lock. Never await another auth call here.
    window.setTimeout(() => { ready = update(session?.user.id ?? 'device'); void ready.catch(() => undefined); }, 0);
  });
  listeningForAuth = true;
  return ready;
}

export async function offlineContext(): Promise<LibraryContext> {
  try {
    await initialiseOfflineAccount();
    if (pending) await pending;
  } catch (error) {
    ready = null;
    throw error;
  }
  return getLibraryContext();
}

export async function assertOfflineContext(expected: LibraryContext): Promise<void> {
  const current = await offlineContext();
  if (current.owner !== expected.owner || current.generation !== expected.generation) {
    throw new Error('Your account changed. Reopen this view before continuing.');
  }
}

/** Clear the offline view before credentials disappear, including server-action deletion. */
export async function clearOfflineAccount(options: { deleteRecording?: boolean } = {}): Promise<void> {
  await pending?.catch(() => undefined);
  const deletingContext = await getLibraryContext();
  const { disableNativeCompanionPush } = await import('@/lib/native/companion-push');
  await disableNativeCompanionPush();
  await getNativeTrackingPlugin()?.stop();
  if (!options.deleteRecording) await checkpointRecording();
  if (options.deleteRecording) {
    const plugin = getNativeTrackingPlugin();
    if (plugin && (await plugin.getStatus()).recoveryAvailable) {
      const value = await plugin.readRecording({ cursor: 0 });
      if (value && typeof value === 'object' && 'recording' in value && value.recording && typeof value.recording === 'object' && 'sessionId' in value.recording && typeof value.recording.sessionId === 'string') {
        await plugin.clearRecording({ sessionId: value.recording.sessionId });
      }
    }
    const { deleteAllLocalData } = await import('@/lib/companion/store');
    await deleteAllLocalData(deletingContext);
    const { deleteAccountOutingCache } = await import("@/lib/companion/outing-store");
    await deleteAccountOutingCache(deletingContext);
    const { deleteAccountStayDrafts } = await import("@/lib/companion/stay-draft-store");
    await deleteAccountStayDrafts(deletingContext);
    const { deleteAccountFeedbackDrafts } = await import("@/lib/companion/outing-feedback-store");
    await deleteAccountFeedbackDrafts(deletingContext);
    const { deleteAccountMemoryDrafts } = await import("@/lib/companion/written-memory-store");
    await deleteAccountMemoryDrafts(deletingContext);
    const { removeClipDraft } = await import("@/lib/companion/freestyle/draft-store");
    await removeClipDraft(deletingContext, null);
    const { removeCommunityDraft } = await import("@/lib/companion/freestyle/community-drafts");
    await removeCommunityDraft(deletingContext, null);
  }
  await setLibraryOwner('device');
  // Even a guest's explicit deletion clears their local downloads.
  const { listLibraryKeys, removeLibraryItem } = await import('@/native-shell/library');
  const context = await getLibraryContext();
  for (const key of await listLibraryKeys(context)) await removeLibraryItem(key, context);
  await clearLegacyDownloads();
  window.dispatchEvent(new Event(OFFLINE_ACCOUNT_EVENT));
}
